import React from 'react';
import { useSelector } from 'react-redux';
import styled from 'styled-components';
import cx from 'classnames';
import checkDone from '@panel/commons/assets/img/checkdone.svg';
import { useLocation, useHistory } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useQuery } from '@panel/commons/hooks';
import { getColor } from '@panel/commons/core/themev3';
import { SavePostingSelectors } from '../../ducks/savePosting';
import { Step1Module1Selectors } from '../../ducks/step1Module1';
import { getStepperState } from '../../ducks/stepper';
import { sendEvent } from '../../utils';

const ProgressItem = styled.div`
  width: 100%;
  max-width: 274px;
  height: 40px;
  border-radius: 10px;
  margin-bottom: 7px;
  background-color: #f3f7f8;
  border: solid 1px #e7eef0;
  padding: 10px;
  padding-left: 15px;
  color: #68808c;
  text-decoration: none;
  display: flex;
  justify-content: space-between;
  cursor: pointer;

  span:first-of-type {
    margin-right: 18px;
  }

  &.disabled-link {
    pointer-events: none;
  }
  &.navigable {
    color: ${getColor('primary')};
    :hover {
      font-weight: 600;
    }
  }
  &.active {
    font-weight: 600;
    background-color: white;
    color: black;
    border: solid 1px white;
    span:first-of-type {
      padding-left: 5px;
      border-left: 2px solid ${getColor('primary')};
    }
  }
  &:last-of-type {
    margin-bottom: 0px;
  }
`;

const CheckComplete = styled.span`
  display: inline-block;
  visibility: hidden;
  background-image: url(${checkDone});
  background-repeat: no-repeat;
  height: 16px;
  width: 16px;
  padding: 5px;
  margin-right: 5px;
  visibility: ${({ isCurrent, isDone }) => (isDone && !isCurrent ? 'visible' : 'hidden')};
`;

const ProgressBoxItem = React.memo(function ProgressBoxItemMemo({
  id,
  title,
  path,
  stepName,
  enablingStep,
  enablingModule,
  preventTransition,
  handleOpenModal
}) {
  const { postingId } = useSelector(SavePostingSelectors.getSavePostingState);
  const { lastStep, lastModule } = useSelector(getStepperState);
  const { operationTypeId, propertyTypeId, subPropertyTypeId, propertyTypes, propertySubTypes } = useSelector(
    Step1Module1Selectors.getInfoEvents
  );
  const [postingIdURL] = useQuery(useLocation(), ['postingId']) || postingId;
  const { pathname } = useLocation();
  const { push } = useHistory();
  const { t } = useTranslation();
  const currentModule = pathname.split('/').pop();

  const isNavigable = () => {
    if (lastStep > enablingStep) return true;
    return parseInt(lastStep) >= parseInt(enablingStep) && parseInt(lastModule) >= parseInt(enablingModule);
  };

  const isDone = () => {
    if (lastStep > enablingStep) return true;
    return parseInt(lastStep) >= parseInt(enablingStep) && parseInt(lastModule) > parseInt(enablingModule);
  };

  const isCurrent = () => currentModule === id;

  const handleRedirect = () => push(path + (postingIdURL ? `?postingId=${postingIdURL}` : ''));

  const moduleNumber = step => {
    let number = '';
    if (currentModule === 'caracteristicas' || currentModule === 'vistaprevia') {
      number = 3;
    } else if (currentModule === 'ubicacion' || currentModule === 'pago') {
      number = 2;
    } else if (currentModule === 'operacionypropiedad' || currentModule === 'seleccion') {
      number = 1;
    }
    const propertyObj = { list: propertyTypes, id: propertyTypeId };
    const subPropertyObj = { list: propertySubTypes, id: subPropertyTypeId };
    sendEvent(`${step} Module${number}`, 'Boton del paso', operationTypeId, propertyObj, subPropertyObj);
  };

  const changeModule = () => (preventTransition ? handleOpenModal(path) : handleRedirect());

  const handleClick = () => {
    moduleNumber(stepName);
    changeModule();
  };

  return (
    <ProgressItem
      data-repa='progressBox-item'
      onClick={handleClick}
      className={cx(
        { done: isDone(), 'disabled-link': !isNavigable(), navigable: isNavigable() },
        pathname === path && 'active'
      )}
    >
      <span data-repa='progressBox-item-title'>{t(title)}</span>
      <CheckComplete data-repa='progressBox-item-check' isCurrent={isCurrent()} isDone={isDone()} />
    </ProgressItem>
  );
});

export { ProgressBoxItem };
